import { defineComponent as _defineComponent } from 'vue';
import { toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, createTextVNode as _createTextVNode, createVNode as _createVNode, withModifiers as _withModifiers, normalizeClass as _normalizeClass, vModelText as _vModelText, withKeys as _withKeys, withDirectives as _withDirectives, createCommentVNode as _createCommentVNode, normalizeStyle as _normalizeStyle, unref as _unref, vShow as _vShow } from "vue";
const _hoisted_1 = ["id"];
const _hoisted_2 = { class: "html-editor__toolbar" };
const _hoisted_3 = ["value", "disabled", "title"];
const _hoisted_4 = { value: "paragraph" };
const _hoisted_5 = ["value"];
const _hoisted_6 = ["onClick", "title"];
const _hoisted_7 = ["title"];
const _hoisted_8 = {
    key: 0,
    class: "html-editor__link"
};
const _hoisted_9 = ["placeholder"];
const _hoisted_10 = ["title"];
const _hoisted_11 = ["title"];
const _hoisted_12 = ["disabled"];
import { computed, nextTick, ref, watch } from "vue";
import { EditorContent, useEditor } from "@tiptap/vue-3";
import StarterKit from "@tiptap/starter-kit";
import { Placeholder } from "@tiptap/extensions";
import { TextAlign } from "@tiptap/extension-text-align";
import { Image } from "@tiptap/extension-image";
import Icon from "../Icon.vue";
export default /*@__PURE__*/ _defineComponent({
    __name: 'HtmlEditor',
    props: {
        modelValue: {},
        disabled: { type: Boolean },
        placeholder: {},
        rows: {},
        id: {}
    },
    emits: [
        'update:modelValue',
        'focus',
        'blur',
    ],
    setup(__props, { emit: __emit }) {
        const HEADING_LEVELS = [1, 2, 3, 4];
        const emit = __emit;
        const props = __props;
        const sourceMode = ref(false);
        const source = ref('');
        const linkEdit = ref(false);
        const linkHref = ref('');
        const linkInput = ref(null);
        const editor = useEditor({
            content: props.modelValue || '',
            editable: !props.disabled,
            extensions: [
                StarterKit.configure({
                    link: {
                        // an editor should not navigate away when a link is clicked
                        openOnClick: false,
                        // tiptap defaults new links to `target="_blank"` and
                        // `rel="noopener noreferrer nofollow"`, which the legacy editor did
                        // not do. `configure` merges deeply, so the defaults have to be
                        // nulled explicitly. Existing links keep what they were saved with.
                        HTMLAttributes: { target: null, rel: null },
                    },
                }),
                TextAlign.configure({ types: ['heading', 'paragraph'] }),
                // legacy content may contain inline and pasted (base64) images
                Image.configure({ inline: true, allowBase64: true }),
                Placeholder.configure({ placeholder: () => props.placeholder || '' }),
            ],
            onUpdate: emitChange,
            onFocus: onFocus,
            onBlur: onBlur,
        });
        const contentStyle = computed(() => ({
            minHeight: (props.rows ? props.rows * 1.5 : 8) + 'rem',
        }));
        const blockFormat = computed(() => {
            const level = HEADING_LEVELS.find(l => isActive('heading', { level: l }));
            return level ? '' + level : 'paragraph';
        });
        const actionGroups = [
            [
                { name: 'bold', icon: 'bold', title: 'form_field_html_bold',
                    active: () => isActive('bold'), run: () => chain()?.toggleBold().run() },
                { name: 'italic', icon: 'italic', title: 'form_field_html_italic',
                    active: () => isActive('italic'), run: () => chain()?.toggleItalic().run() },
                { name: 'underline', icon: 'underline', title: 'form_field_html_underline',
                    active: () => isActive('underline'), run: () => chain()?.toggleUnderline().run() },
                { name: 'strike', icon: 'strikethrough', title: 'form_field_html_strikethrough',
                    active: () => isActive('strike'), run: () => chain()?.toggleStrike().run() },
                { name: 'clear', icon: 'remove-format', title: 'form_field_html_clear_format',
                    active: () => false, run: () => chain()?.unsetAllMarks().run() },
            ],
            [
                { name: 'bullet-list', icon: 'list-ul', title: 'form_field_html_list_bullet',
                    active: () => isActive('bulletList'), run: () => chain()?.toggleBulletList().run() },
                { name: 'ordered-list', icon: 'list-ol', title: 'form_field_html_list_ordered',
                    active: () => isActive('orderedList'), run: () => chain()?.toggleOrderedList().run() },
            ],
            [
                { name: 'link', icon: 'link', title: 'form_field_html_link',
                    active: () => isActive('link'), run: editLink },
                { name: 'unlink', icon: 'unlink', title: 'form_field_html_unlink',
                    active: () => false, run: () => chain()?.extendMarkRange('link').unsetLink().run() },
            ],
        ];
        // changes from the outside
        watch(() => props.modelValue, value => {
            const instance = editor.value;
            if (!instance || sourceMode.value) {
                return;
            }
            const next = value || '';
            // `emitChange` reports an empty document as `undefined`, so an empty
            // incoming value must not be compared against the `<p></p>` of `toHtml()`
            if (next === toHtml(instance) || (!next && instance.isEmpty)) {
                return;
            }
            instance.commands.setContent(next, { emitUpdate: false });
        });
        watch(() => props.disabled, value => editor.value?.setEditable(!value));
        // changes from the inside
        function emitChange() {
            const instance = editor.value;
            if (!instance) {
                return;
            }
            emit('update:modelValue', instance.isEmpty ? undefined : toHtml(instance));
        }
        /**
         * Cleans up the two bits of markup tiptap's document model adds but the stored
         * content should not carry. Neither changes how the html reads back in.
         *
         * 1. A list item is `li > p` (the nested-list case needs the block content), so
         *    `getHTML()` writes `<li><p>item</p></li>`. The legacy editor wrote
         *    `<li>item</li>`, which is what the stored content and the shop templates
         *    look like, so the leading paragraph is unwrapped again. Any further
         *    paragraphs of the same item are kept.
         * 2. `TrailingNode` appends an empty paragraph after a trailing block (a list,
         *    a heading) so there is something to click below it. That is an editing
         *    affordance, not content, so the last one is dropped.
         */
        function toHtml(instance) {
            const container = document.createElement('div');
            container.innerHTML = instance.getHTML();
            container.querySelectorAll('li > p:first-child').forEach(paragraph => {
                paragraph.replaceWith(...paragraph.childNodes);
            });
            const last = container.lastElementChild;
            if (last && last.tagName === 'P' && !last.hasChildNodes()) {
                last.remove();
            }
            return container.innerHTML;
        }
        function chain() {
            return editor.value?.chain().focus();
        }
        function isActive(name, attributes) {
            return editor.value?.isActive(name, attributes) ?? false;
        }
        function setBlockFormat(value) {
            const commands = chain();
            if (!commands) {
                return;
            }
            if (value === 'paragraph') {
                commands.setParagraph().run();
            }
            else {
                commands.setNode('heading', { level: parseInt(value, 10) }).run();
            }
        }
        function editLink() {
            linkHref.value = editor.value?.getAttributes('link').href || '';
            linkEdit.value = true;
            nextTick(() => linkInput.value?.focus());
        }
        function applyLink() {
            const commands = chain();
            const href = linkHref.value;
            linkEdit.value = false;
            if (!commands) {
                return;
            }
            if (!href) {
                commands.extendMarkRange('link').unsetLink().run();
            }
            else if (editor.value.state.selection.empty && !isActive('link')) {
                // nothing selected: insert the url as its own link, like TinyMCE did
                commands.insertContent({ type: 'text', text: href, marks: [{ type: 'link', attrs: { href } }] }).run();
            }
            else {
                commands.extendMarkRange('link').setLink({ href }).run();
            }
        }
        function cancelLink() {
            linkEdit.value = false;
            editor.value?.commands.focus();
        }
        function toggleSource() {
            const instance = editor.value;
            if (!instance) {
                return;
            }
            if (!sourceMode.value) {
                // show what would be saved, not the schema-filtered round trip
                source.value = props.modelValue || '';
                sourceMode.value = true;
                return;
            }
            instance.commands.setContent(source.value, { emitUpdate: false });
            sourceMode.value = false;
            // the round trip through the schema may have normalized the markup
            emitChange();
        }
        function onSourceInput() {
            const value = source.value;
            emit('update:modelValue', value ? value : undefined);
        }
        function onFocus() {
            emit('focus');
        }
        function onBlur() {
            emit('blur');
        }
        return (_ctx, _cache) => {
            return (_openBlock(), _createElementBlock("div", {
                class: "html-editor",
                id: __props.id
            }, [
                _createElementVNode("div", _hoisted_2, [
                    _createElementVNode("select", {
                        value: blockFormat.value,
                        onChange: _cache[0] || (_cache[0] = ($event) => (setBlockFormat($event.target.value))),
                        disabled: __props.disabled || sourceMode.value || undefined,
                        title: _ctx.$t('form_field_html_block'),
                        class: "html-editor__block"
                    }, [
                        _createElementVNode("option", _hoisted_4, _toDisplayString(_ctx.$t('form_field_html_block_paragraph')), 1),
                        _cache[12] || (_cache[12] = _createTextVNode()),
                        (_openBlock(), _createElementBlock(_Fragment, null, _renderList(HEADING_LEVELS, (level) => {
                            return _createElementVNode("option", {
                                key: level,
                                value: level
                            }, _toDisplayString(_ctx.$t('form_field_html_block_heading', { level })), 9, _hoisted_5);
                        }), 64))
                    ], 40, _hoisted_3),
                    _cache[15] || (_cache[15] = _createTextVNode()),
                    (_openBlock(), _createElementBlock(_Fragment, null, _renderList(actionGroups, (group, index) => {
                        return (_openBlock(), _createElementBlock(_Fragment, { key: index }, [
                            _cache[13] || (_cache[13] = _createElementVNode("span", { class: "html-editor__separator" }, null, -1)),
                            _cache[14] || (_cache[14] = _createTextVNode()),
                            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(group, (action) => {
                                return (_openBlock(), _createElementBlock("a", {
                                    key: action.name,
                                    onMousedown: _cache[1] || (_cache[1] = _withModifiers(() => { }, ["prevent"])),
                                    onClick: _withModifiers(($event) => (action.run()), ["prevent"]),
                                    href: "",
                                    class: _normalizeClass(["html-editor__action", {
                                            'html-editor__action--active': action.active(),
                                            'html-editor__action--disabled': __props.disabled || sourceMode.value,
                                        }]),
                                    title: _ctx.$t(action.title)
                                }, [
                                    _createVNode(Icon, {
                                        name: action.icon,
                                        class: "html-editor__icon"
                                    }, null, 8, ["name"])
                                ], 42, _hoisted_6));
                            }), 128))
                        ], 64));
                    }), 64)),
                    _cache[16] || (_cache[16] = _createTextVNode()),
                    _cache[17] || (_cache[17] = _createElementVNode("span", { class: "html-editor__separator" }, null, -1)),
                    _cache[18] || (_cache[18] = _createTextVNode()),
                    _createElementVNode("a", {
                        onMousedown: _cache[2] || (_cache[2] = _withModifiers(() => { }, ["prevent"])),
                        onClick: _cache[3] || (_cache[3] = _withModifiers(($event) => (toggleSource()), ["prevent"])),
                        href: "",
                        class: _normalizeClass(["html-editor__action", { 'html-editor__action--active': sourceMode.value }]),
                        title: _ctx.$t('form_field_html_source')
                    }, [
                        _createVNode(Icon, {
                            name: "code",
                            class: "html-editor__icon"
                        })
                    ], 42, _hoisted_7)
                ]),
                _cache[21] || (_cache[21] = _createTextVNode()),
                (linkEdit.value)
                    ? (_openBlock(), _createElementBlock("div", _hoisted_8, [
                        _withDirectives(_createElementVNode("input", {
                            "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event) => ((linkHref).value = $event)),
                            onKeydown: [
                                _cache[5] || (_cache[5] = _withKeys(_withModifiers(($event) => (applyLink()), ["prevent"]), ["enter"])),
                                _cache[6] || (_cache[6] = _withKeys(_withModifiers(($event) => (cancelLink()), ["prevent"]), ["esc"]))
                            ],
                            ref_key: "linkInput",
                            ref: linkInput,
                            type: "url",
                            placeholder: _ctx.$t('form_field_html_link_url'),
                            autocomplete: "off",
                            class: "html-editor__link-input"
                        }, null, 40, _hoisted_9), [
                            [
                                _vModelText,
                                linkHref.value,
                                void 0,
                                { trim: true }
                            ]
                        ]),
                        _cache[19] || (_cache[19] = _createTextVNode()),
                        _createElementVNode("a", {
                            onMousedown: _cache[7] || (_cache[7] = _withModifiers(() => { }, ["prevent"])),
                            onClick: _cache[8] || (_cache[8] = _withModifiers(($event) => (applyLink()), ["prevent"])),
                            href: "",
                            class: "html-editor__action",
                            title: _ctx.$t('save')
                        }, [
                            _createVNode(Icon, {
                                name: "check",
                                class: "html-editor__icon"
                            })
                        ], 40, _hoisted_10),
                        _cache[20] || (_cache[20] = _createTextVNode()),
                        _createElementVNode("a", {
                            onMousedown: _cache[9] || (_cache[9] = _withModifiers(() => { }, ["prevent"])),
                            onClick: _cache[10] || (_cache[10] = _withModifiers(($event) => (cancelLink()), ["prevent"])),
                            href: "",
                            class: "html-editor__action",
                            title: _ctx.$t('cancel')
                        }, [
                            _createVNode(Icon, {
                                name: "cancel",
                                class: "html-editor__icon"
                            })
                        ], 40, _hoisted_11)
                    ]))
                    : _createCommentVNode("", true),
                _cache[22] || (_cache[22] = _createTextVNode()),
                (sourceMode.value)
                    ? _withDirectives((_openBlock(), _createElementBlock("textarea", {
                        key: 1,
                        "onUpdate:modelValue": _cache[11] || (_cache[11] = ($event) => ((source).value = $event)),
                        onInput: onSourceInput,
                        onFocus: onFocus,
                        onBlur: onBlur,
                        disabled: __props.disabled || undefined,
                        style: _normalizeStyle(contentStyle.value),
                        class: "html-editor__source"
                    }, null, 44, _hoisted_12)), [
                        [_vModelText, source.value]
                    ])
                    : _createCommentVNode("", true),
                _cache[23] || (_cache[23] = _createTextVNode()),
                _withDirectives(_createVNode(_unref(EditorContent), {
                    editor: _unref(editor),
                    style: _normalizeStyle(contentStyle.value),
                    class: "html-editor__content"
                }, null, 8, ["editor", "style"]), [
                    [_vShow, !sourceMode.value]
                ])
            ], 8, _hoisted_1));
        };
    }
});
